import { Link, type Href } from 'expo-router';
import { ArrowLeftIcon } from 'lucide-react-native';
import { ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BRAND } from '@sixam/config/brand';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';

export type LegalSection = Readonly<{
  body: readonly string[];
  title: string;
}>;

export type LegalPageAction = Readonly<{
  href: Href;
  label: string;
  variant?: 'default' | 'secondary';
}>;

export function LegalPage({
  actions = [],
  intro,
  sections,
  title,
  updatedAt,
}: Readonly<{
  actions?: readonly LegalPageAction[];
  intro: string;
  sections: readonly LegalSection[];
  title: string;
  updatedAt: string;
}>) {
  const insets = useSafeAreaInsets();

  return (
    <ScrollView
      className="flex-1 bg-background"
      contentContainerClassName="pb-16"
      contentInsetAdjustmentBehavior="automatic"
    >
      <View
        className="gap-5 px-5 pb-7 sm:px-8"
        style={{ backgroundColor: BRAND.color.ink, paddingTop: Math.max(insets.top, 24) + 12 }}
      >
        <Link href="/" asChild>
          <Button accessibilityLabel="Back to 6am" size="icon" variant="outline">
            <Icon as={ArrowLeftIcon} className="size-5 text-white" />
          </Button>
        </Link>
        <View className="gap-2">
          <Text variant="label" className="text-primary">
            6am BKK
          </Text>
          <Text variant="h1" className="text-white">
            {title}
          </Text>
          <Text className="max-w-3xl text-white/80">{intro}</Text>
          <Text variant="mono" className="text-white/70">
            Updated {updatedAt}
          </Text>
        </View>
      </View>

      <View className="mx-auto w-full max-w-3xl gap-8 px-5 py-8 sm:px-8">
        {sections.map((section) => (
          <View className="gap-3 border-b border-border pb-8" key={section.title}>
            <Text variant="h3">{section.title}</Text>
            {section.body.map((paragraph) => (
              <Text className="leading-6" key={paragraph} selectable>
                {paragraph}
              </Text>
            ))}
          </View>
        ))}

        {actions.length > 0 ? (
          <View className="gap-3">
            {actions.map((action) => (
              <Link href={action.href} asChild key={action.label}>
                <Button variant={action.variant ?? 'default'}>
                  <Text>{action.label}</Text>
                </Button>
              </Link>
            ))}
          </View>
        ) : null}
      </View>
    </ScrollView>
  );
}
